import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { toastHandler } from "../../../../../utils/helpers";
import { toastTypesKeys } from "../../../../../utils/constants";
import { ToastContainer } from "react-toastify";

const CREATE_THEMES = gql`
  mutation CreateTheme($input: ThemeCreateInput!) {
    themeCreate(input: $input) {
      clientMutationId
      theme {
        id
        title
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_THEME = gql`
  query GetTheme($id: ID!) {
    theme(id: $id) {
      id
      title
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_THEME = gql`
  mutation UpdateTheme($id: ID!, $title: String!) {
    themeUpdate(input: { id: $id, themeInput: { title: $title } }) {
      clientMutationId
      theme {
        createdAt
        id
        title
        updatedAt
      }
    }
  }
`;

function ThemesAction({ isUpdate = false }) {
  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const { translator, currentLanguage } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const theme_id = searchParams.get("id");

  const { data: themeData } = useQuery(GET_THEME, {
    variables: { id: theme_id },
    skip: !theme_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createThemes, { data, loading, error }] = useMutation(CREATE_THEMES, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [updateTheme] = useMutation(UPDATE_THEME, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!themeData?.theme) return undefined;

    const { title } = themeData?.theme;

    return {
      theme_name: title,
    };
  }, [themeData]);

  const form = useForm<any>({
    defaultValues: {
      theme_name: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateTheme({
          variables: {
            id: Number(theme_id),
            title: data?.theme_name,
          },
        });
        if (response.data) {
          notify.success(`Theme Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/themes");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }
    try {
      const response = await createThemes({
        variables: { input: { themeInput: { title: data?.theme_name } } },
      });
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Theme Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/themes");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Theme Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel>
                {" "}
                {translator("Theme")} {translator("dashboardTableHead.Name")}
              </FormLabel>
              <OutlinedInput
                {...form?.register("theme_name", { required: "Required!" })}
              />
              {/* <FormHelperText>{errors.title?.message}</FormHelperText> */}
            </FormControl>
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/themes"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </>
  );
}

export { ThemesAction };
